import { useRef, useState } from "react";
import { Loader2, Paperclip, FileText } from "lucide-react";
import { toast } from "sonner";
import { Label } from "../components/ui/label";
import { fileUrl, formatErr, label, uploadFile } from "../lib/api";

const COLORS = {
  pending: "bg-amber-500/15 text-amber-300 border-amber-400/30",
  sent: "bg-sky-500/15 text-sky-300 border-sky-400/30",
  accepted: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  declined: "bg-rose-500/15 text-rose-300 border-rose-400/30",
  rejected: "bg-rose-500/15 text-rose-300 border-rose-400/30",
  awaiting_payment: "bg-amber-500/15 text-amber-300 border-amber-400/30",
  confirmed: "bg-sky-500/15 text-sky-300 border-sky-400/30",
  in_progress: "bg-[#007bff]/20 text-[#5cc8ff] border-[#007bff]/40",
  ready_for_review: "bg-violet-500/15 text-violet-300 border-violet-400/30",
  completed: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  cancelled: "bg-slate-500/15 text-slate-300 border-slate-400/30",
  active: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  suspended: "bg-rose-500/15 text-rose-300 border-rose-400/30",
  deactivated: "bg-slate-500/15 text-slate-300 border-slate-400/30",
  unpaid: "bg-slate-500/15 text-slate-300 border-slate-400/30",
  submitted: "bg-amber-500/15 text-amber-300 border-amber-400/30",
  new: "bg-amber-500/15 text-amber-300 border-amber-400/30",
  resolved: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
};

export const StatusBadge = ({ s, testid }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap ${COLORS[s] || COLORS.cancelled}`} data-testid={testid}>{label(s)}</span>
);

export const Online = ({ on }) => <span className={`inline-block w-2.5 h-2.5 rounded-full ${on ? "bg-emerald-400 shadow-[0_0_8px_#34d399]" : "bg-slate-600"}`} title={on ? "Online" : "Offline"} />;

export function PageHeader({ title, desc, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
      <div>
        <h1 className="font-display text-4xl sm:text-5xl text-metal" data-testid="page-title">{title}</h1>
        {desc && <p className="text-slate-400 mt-1">{desc}</p>}
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

export const StatCard = ({ label: l, value, icon: I, testid }) => (
  <div className="card-neon p-5" data-testid={testid}>
    <div className="flex items-center justify-between"><p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">{l}</p>{I && <I className="w-5 h-5 text-[#00d9ff]" />}</div>
    <p className="font-display text-5xl text-electric mt-2">{value ?? "–"}</p>
  </div>
);

export const Empty = ({ text }) => <div className="card-neon p-10 text-center text-slate-400" data-testid="empty-state">{text}</div>;

export const Field = ({ label: l, children, full }) => (
  <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}><Label className="text-xs uppercase tracking-wider text-slate-400">{l}</Label>{children}</div>
);

export const inputCls = "bg-[#040a1a] border-[#007bff]/35";

export function UploadButton({ onUploaded, accept = "image/*", scope = "private", convId, orderId, children, className = "", testid, multiple }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const onChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const out = [];
      for (const f of files) out.push(await uploadFile(f, { scope, convId, orderId }));
      onUploaded(multiple ? out : out[0]);
    } catch (err) {
      toast.error(formatErr(err));
    }
    setBusy(false);
    e.target.value = "";
  };
  return (
    <>
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} className={className || "btn-ghost !py-2 !px-3 text-sm"} data-testid={testid}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : children || <><Paperclip className="w-4 h-4" /> Upload</>}
      </button>
      <input ref={ref} type="file" accept={accept} hidden multiple={multiple} onChange={onChange} />
    </>
  );
}

export function Attachments({ items = [] }) {
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {items.map((a, i) =>
        a.content_type?.startsWith("image/") ? (
          <a key={i} href={fileUrl(a.url)} target="_blank" rel="noreferrer"><img src={fileUrl(a.url)} alt={a.name} className="w-28 h-20 object-cover rounded-md border border-[#007bff]/30" /></a>
        ) : (
          <a key={i} href={fileUrl(a.url)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs px-2 py-1.5 rounded-md bg-[#061232] border border-[#007bff]/30 text-[#7fd8ff]"><FileText className="w-4 h-4" />{a.name}</a>
        )
      )}
    </div>
  );
}
